import React, { useEffect, useState } from 'react';
import { casinoApi, casinoGroupApi } from '../services/api';
import { Casino, CasinoGroup } from '../types';
import './Casinos.css';

interface CasinoFormData {
  name: string;
  url: string;
  logo_url: string;
  group_id: number | null;
}

const Casinos: React.FC = () => {
  const [casinos, setCasinos] = useState<Casino[]>([]);
  const [groups, setGroups] = useState<CasinoGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Form state for new casino
  const [newCasino, setNewCasino] = useState<CasinoFormData>({
    name: '',
    url: '',
    logo_url: '',
    group_id: null
  });

  // Edit modal state
  const [editingCasino, setEditingCasino] = useState<Casino | null>(null);
  const [editFormData, setEditFormData] = useState<CasinoFormData>({
    name: '',
    url: '',
    logo_url: '',
    group_id: null
  });

  // Group management
  const [newGroupName, setNewGroupName] = useState('');
  const [showGroupManager, setShowGroupManager] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [casinosRes, groupsRes] = await Promise.all([
        casinoApi.getAll(),
        casinoGroupApi.getAll().catch(() => ({ data: [] }))
      ]);
      setCasinos(Array.isArray(casinosRes.data) ? casinosRes.data : []);
      setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCasino = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newCasino.name.trim()) return;

    try {
      const response = await casinoApi.create({
        name: newCasino.name.trim(),
        url: newCasino.url.trim() || undefined,
        logo_url: newCasino.logo_url.trim() || undefined,
        group_id: newCasino.group_id || undefined
      });
      setCasinos([...casinos, response.data]);
      setNewCasino({ name: '', url: '', logo_url: '', group_id: null });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Erstellen');
    }
  };

  const handleEditClick = (casino: Casino) => {
    setEditingCasino(casino);
    setEditFormData({
      name: casino.name,
      url: casino.url || '',
      logo_url: casino.logo_url || '',
      group_id: casino.group_id
    });
  };

  const handleUpdateCasino = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCasino) return;
    setError('');

    try {
      const response = await casinoApi.update(editingCasino.id, {
        name: editFormData.name.trim(),
        url: editFormData.url.trim() || undefined,
        logo_url: editFormData.logo_url.trim() || undefined,
        group_id: editFormData.group_id || undefined
      });

      setCasinos(casinos.map(c =>
        c.id === editingCasino.id
          ? { ...c, ...response.data, group_name: groups.find(g => g.id === editFormData.group_id)?.name || null }
          : c
      ));
      setEditingCasino(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Aktualisieren');
    }
  };

  const handleDeleteCasino = async (id: number) => {
    if (!window.confirm('Casino wirklich löschen? Alle zugehörigen Transaktionen werden ebenfalls gelöscht!')) {
      return;
    }

    try {
      await casinoApi.delete(id);
      setCasinos(casinos.filter(c => c.id !== id));
    } catch (error) {
      console.error('Error deleting casino:', error);
    }
  };

  const handleAddGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;

    try {
      const response = await casinoGroupApi.create(newGroupName.trim());
      setGroups([...groups, response.data]);
      setNewGroupName('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Erstellen der Gruppe');
    }
  };

  const handleDeleteGroup = async (id: number) => {
    if (!window.confirm('Gruppe wirklich löschen?')) return;

    try {
      await casinoGroupApi.delete(id);
      setGroups(groups.filter(g => g.id !== id));
    } catch (error) {
      console.error('Error deleting group:', error);
    }
  };

  // Group casinos by group
  const groupedCasinos = casinos.reduce((acc, casino) => {
    const groupName = casino.group_name || 'Ohne Gruppe';
    if (!acc[groupName]) acc[groupName] = [];
    acc[groupName].push(casino);
    return acc;
  }, {} as Record<string, Casino[]>);

  if (isLoading) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="casinos-page">
      <div className="casinos-header">
        <h1>Casinos verwalten</h1>
        <button
          className="btn-manage-groups"
          onClick={() => setShowGroupManager(!showGroupManager)}
        >
          {showGroupManager ? 'Schließen' : 'Gruppen verwalten'}
        </button>
      </div>

      {/* Group Manager */}
      {showGroupManager && (
        <div className="group-manager">
          <h3>Casino-Gruppen</h3>
          <form onSubmit={handleAddGroup} className="add-group-form">
            <input
              type="text"
              placeholder="Neue Gruppe..."
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
            />
            <button type="submit" className="btn-add-small">+</button>
          </form>
          <div className="groups-list">
            {groups.map(group => (
              <div key={group.id} className="group-item">
                <span>{group.name}</span>
                <button onClick={() => handleDeleteGroup(group.id)} className="btn-delete-small">×</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Casino Form */}
      <div className="add-casino-form">
        <h3>Neues Casino hinzufügen</h3>
        <form onSubmit={handleAddCasino}>
          <div className="form-row">
            <input
              type="text"
              placeholder="Casino Name *"
              value={newCasino.name}
              onChange={(e) => setNewCasino({ ...newCasino, name: e.target.value })}
              required
            />
            <select
              value={newCasino.group_id || ''}
              onChange={(e) => setNewCasino({ ...newCasino, group_id: e.target.value ? parseInt(e.target.value) : null })}
            >
              <option value="">Keine Gruppe</option>
              {groups.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <input
              type="url"
              placeholder="Casino URL (https://...)"
              value={newCasino.url}
              onChange={(e) => setNewCasino({ ...newCasino, url: e.target.value })}
            />
            <input
              type="url"
              placeholder="Logo URL (https://...)"
              value={newCasino.logo_url}
              onChange={(e) => setNewCasino({ ...newCasino, logo_url: e.target.value })}
            />
          </div>
          <button type="submit" className="btn-add">Casino hinzufügen</button>
        </form>
        {error && <p className="error-text">{error}</p>}
      </div>

      {/* Casinos List grouped by group */}
      <div className="casinos-grouped">
        {Object.entries(groupedCasinos).map(([groupName, groupCasinos]) => (
          <div key={groupName} className="casino-group-section">
            <h3 className="group-title">{groupName}</h3>
            <div className="casinos-list">
              {groupCasinos.map((casino) => (
                <div key={casino.id} className="casino-item">
                  <div className="casino-info">
                    {(casino.cached_logo || casino.logo_url) && (
                      <img
                        src={casino.cached_logo || casino.logo_url || ''}
                        alt={casino.name}
                        className="casino-logo"
                        onError={(e) => (e.target as HTMLImageElement).style.display = 'none'}
                      />
                    )}
                    <div className="casino-details">
                      <span className="casino-name">{casino.name}</span>
                      {casino.url && (
                        <a href={casino.url} target="_blank" rel="noopener noreferrer" className="casino-url">
                          {casino.url}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="casino-actions">
                    <button onClick={() => handleEditClick(casino)} className="btn-edit">
                      Bearbeiten
                    </button>
                    <button onClick={() => handleDeleteCasino(casino.id)} className="btn-delete">
                      Löschen
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {casinos.length === 0 && (
        <p className="no-data">Noch keine Casinos hinzugefügt</p>
      )}

      {/* Edit Modal */}
      {editingCasino && (
        <div className="modal-overlay" onClick={() => setEditingCasino(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Casino bearbeiten</h2>
            <form onSubmit={handleUpdateCasino}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Gruppe</label>
                <select
                  value={editFormData.group_id || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, group_id: e.target.value ? parseInt(e.target.value) : null })}
                >
                  <option value="">Keine Gruppe</option>
                  {groups.map(group => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>URL</label>
                <input
                  type="url"
                  value={editFormData.url}
                  onChange={(e) => setEditFormData({ ...editFormData, url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div className="form-group">
                <label>Logo URL</label>
                <input
                  type="url"
                  value={editFormData.logo_url}
                  onChange={(e) => setEditFormData({ ...editFormData, logo_url: e.target.value })}
                  placeholder="https://..."
                />
                {editFormData.logo_url && (
                  <img
                    src={editFormData.logo_url}
                    alt="Preview"
                    className="logo-preview"
                    onError={(e) => (e.target as HTMLImageElement).style.display = 'none'}
                  />
                )}
              </div>
              {error && <p className="error-text">{error}</p>}
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setEditingCasino(null)}>
                  Abbrechen
                </button>
                <button type="submit" className="btn-save">
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Casinos;
